import { api } from 'shared/api/client';
import { apiErrorText } from 'shared/api/error-message';
import ServerErrorHandler from 'shared/feedback/ErrorHandler';
import { useQueryClient, useMutation, useQuery, UseQueryResult, UseMutationResult } from '@tanstack/react-query';
import { UUID } from './types';
import { dispatchMessage, EventType } from 'shared/feedback/toasts';
import { i18n } from 'shared/i18n';

export const mandatoryQueryKeys = {
  concepts: 'MANDATORY_CONCEPTS',
  progress: 'MANDATORY_PROGRESS',
  status: 'MANDATORY_STATUS',
} as const;

export interface MandatoryConcept {
  id: UUID;
  name: string;
  labels: Record<string, Record<string, string>>;
}

export interface MandatoryProgress {
  total: number;
  tagged: number;
  dismissed: number;
  addressed: number;
  percentage: number;
}

export interface MandatoryDismissal {
  message: string;
  dismissedBy: number;
  dismissedAt: string;
}

export interface MandatoryStatusEntry {
  conceptId: UUID;
  name: string;
  labels: Record<string, Record<string, string>>;
  tagged: boolean;
  dismissal: MandatoryDismissal | null;
}

export function useMandatoryConcepts(taxonomyId: UUID | undefined): UseQueryResult<MandatoryConcept[]> {
  return useQuery({
    queryKey: [mandatoryQueryKeys.concepts, taxonomyId],

    queryFn: async () => {
      const res = await api.GET('/mandatory-tags/taxonomy/{taxonomyId}/concepts', { params: { path: { taxonomyId: taxonomyId as string } } });
      return res?.data || [];
    },

    enabled: !!taxonomyId,
  });
}

export function useMandatoryProgress(documentSuperId: number | undefined, taxonomyId: UUID | undefined): UseQueryResult<MandatoryProgress> {
  return useQuery({
    queryKey: [mandatoryQueryKeys.progress, documentSuperId, taxonomyId],

    queryFn: async () => {
      const res = await api.GET('/mandatory-tags/document/{documentSuperId}/progress', {
        params: { path: { documentSuperId: String(documentSuperId) }, query: { taxonomyId: taxonomyId as string } },
      });
      return res?.data;
    },

    enabled: !!documentSuperId && !!taxonomyId,
  });
}

export function useMandatoryStatus(documentSuperId: number | undefined, taxonomyId: UUID | undefined): UseQueryResult<MandatoryStatusEntry[]> {
  return useQuery({
    queryKey: [mandatoryQueryKeys.status, documentSuperId, taxonomyId],

    queryFn: async () => {
      const res = await api.GET('/mandatory-tags/document/{documentSuperId}/status', {
        params: { path: { documentSuperId: String(documentSuperId) }, query: { taxonomyId: taxonomyId as string } },
      });
      return res?.data || [];
    },

    enabled: !!documentSuperId && !!taxonomyId,
  });
}

export interface DismissMandatoryInput {
  documentSuperId: number;
  conceptId: UUID;
  message: string;
}

export function useDismissMandatoryConcept(): UseMutationResult<unknown, unknown, DismissMandatoryInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ documentSuperId, conceptId, message }: DismissMandatoryInput) => {
      const res = await api.POST('/mandatory-tags/document/{documentSuperId}/dismissals', {
        params: { path: { documentSuperId: String(documentSuperId) } },
        body: { conceptId, message },
      });
      return res?.data;
    },

    onError: (error: unknown) => {
      const errorMessage = apiErrorText(error, i18n('tag:mandatory.dismiss.error'));
      dispatchMessage(EventType.ADD_MESSAGE, { type: 'error', text: errorMessage, removeTime: 4000 });
    },

    onSuccess: (_data, variables) => {
      dispatchMessage(EventType.ADD_MESSAGE, {
        type: 'success',
        text: i18n('tag:mandatory.dismiss.success'),
        removeTime: 4000,
      });
      queryClient.invalidateQueries({
        queryKey: [mandatoryQueryKeys.progress, variables.documentSuperId],
      });
      queryClient.invalidateQueries({
        queryKey: [mandatoryQueryKeys.status, variables.documentSuperId],
      });
    },
  });
}

export interface UndismissMandatoryInput {
  documentSuperId: number;
  conceptId: UUID;
}

export function useUndismissMandatoryConcept(): UseMutationResult<unknown, unknown, UndismissMandatoryInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ documentSuperId, conceptId }: UndismissMandatoryInput) => {
      const res = await api.DELETE('/mandatory-tags/document/{documentSuperId}/dismissals/{conceptId}', {
        params: { path: { documentSuperId: String(documentSuperId), conceptId } },
      });
      return res?.data;
    },

    onError: ServerErrorHandler,

    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [mandatoryQueryKeys.progress, variables.documentSuperId],
      });
      queryClient.invalidateQueries({
        queryKey: [mandatoryQueryKeys.status, variables.documentSuperId],
      });
    },
  });
}
